import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { getAllCategories, getAllPosts, getAllTags, getPostsCount } from '@lib/wordpress';
import { Category, Post, Tag } from '@lib/wordpress.d';
import { BlogList } from '@modules/blog/list/BlogList';

type Props = {
  posts: Post[];
  categories: Category[];
  tags: Tag[];
  totalPosts: number;
};

const index = ({ posts, categories, tags, totalPosts }: Props) => {
  const router = useRouter();
  const pageParam = router.query?.page as string;
  const selectedCategory = router.query?.category as string;

  if (router.isFallback || posts === undefined) return <div>Loading...</div>;

  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const postsPerPage = 9;
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  let TITLE = 'Expert Care for Your Property | Qeeper';
  let DESC =
    'Expert property maintenance guides and tips to protect your investment. Practical solutions for homeowners, landlords, and property managers.';
  let URL = 'https://www.qeeper.co.uk/blog';

  if (pageParam && selectedCategory) {
    TITLE = `Expert Care for Your Property | Page ${page} Category ${selectedCategory} | Qeeper`;
    DESC = `Page ${page} Category ${selectedCategory} - ${DESC}`;
    URL = `${URL}?page=${page}&category=${selectedCategory}`;
  } else if (selectedCategory) {
    TITLE = `Expert Care for Your Property | Category ${selectedCategory} | Qeeper`;
    DESC = `Category ${selectedCategory} - ${DESC}`;
    URL = `${URL}?category=${selectedCategory}`;
  } else if (pageParam) {
    TITLE = `Expert Care for Your Property | Page ${page} | Qeeper`;
    DESC = `Page ${page} - ${DESC}`;
    URL = `${URL}?page=${page}`;
  }

  return (
    <>
      <Head>
        <link rel="canonical" key="canonical" href={URL} />
        <link rel="alternate" hrefLang="en" href={URL} />
      </Head>
      <NextSeo
        title={TITLE}
        description={DESC}
        canonical={URL}
        openGraph={{
          url: URL,
          title: TITLE,
          description: DESC,
        }}
      />
      <BlogList
        posts={posts}
        categories={categories}
        page={page}
        totalPages={totalPages}
        tags={tags}
        selectedCategory={selectedCategory}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (props) => {
  props.res.setHeader('Cache-Control', 'public, s-maxage=600, stale-while-revalidate=60');

  let category: any = props?.query?.category;
  let tag: any = props?.query?.tag;
  const page = props?.query?.page ? parseInt(props.query.page as string, 10) : 1;

  if (!props?.query?.category || props?.query?.category === 'all') {
    category = undefined;
  }

  if (!props?.query?.tag || props?.query?.tag === 'all') {
    tag = undefined;
  }

  const postsPerPage = 9;

  // Only fetch the fields we need for the listing page to reduce data size
  const fields = 'id,slug,title,excerpt,date,categories,tags,yoast_head_json';

  // Fetch only the posts for the current page from WordPress API
  const posts = await getAllPosts({
    category,
    tag,
    per_page: postsPerPage,
    page: page,
    fields: fields,
  });

  // Get total count from WordPress API headers
  const totalPosts = await getPostsCount({
    category,
    tag,
  });

  const categories = await getAllCategories();
  const tags = await getAllTags();

  return {
    props: {
      posts,
      categories,
      tags,
      totalPosts,
    },
  };
};

export default index;
